/**
 * Call Manager — modal/popover with Dialer, History, and Settings tabs.
 * Anchored to the Phone icon in InboxNav.
 */

import { useRef, useEffect, useMemo } from 'react';
import { X, Phone, Clock, Settings, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isLiveCallStatus } from '@/types/voice';
import { useCallStore } from '@/stores/call-store';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { Dialer } from './dialer';
import { CallHistory } from './call-history';
import { VoiceSettings } from './voice-settings';
import { AgentAvailabilityList } from './agent-availability';

const ALL_TABS = [
  { id: 'dialer' as const, label: 'Dialer', icon: Phone },
  { id: 'history' as const, label: 'History', icon: Clock },
  // { id: 'agents' as const, label: 'Agents', icon: Users }, // TODO: re-enable once CF writes agent statuses
  { id: 'settings' as const, label: 'Settings', icon: Settings },
];

export function CallManager() {
  const isOpen = useCallStore((s) => s.callManagerOpen);
  const toggleCallManager = useCallStore((s) => s.toggleCallManager);
  const activeCalls = useCallStore((s) => s.activeCalls);

  // Don't show Call Manager when floating call widget is active
  const hasActiveCalls = activeCalls.some((c) => isLiveCallStatus(c.status));
  const activeTab = useCallStore((s) => s.callManagerTab);
  const setTab = useCallStore((s) => s.setCallManagerTab);

  // Availability
  const isAvailable = useCallStore((s) => s.isAvailable);
  const toggleAvailability = useCallStore((s) => s.toggleAvailability);
  const softphoneState = useCallStore((s) => s.softphoneState);
  const availabilityUpdating = useCallStore((s) => s.availabilityUpdating);

  // Agent statuses — temporarily disabled until CF writes agent statuses
  // const agentStatuses = useCallStore((s) => s.agentStatuses);
  // const loadAgentStatuses = useCallStore((s) => s.loadAgentStatuses);

  // Fresh-fetch phone numbers & config when dialog opens
  const refreshVoiceConfig = useCallStore((s) => s.refreshVoiceConfig);

  // Check if we're in inbox view (sidebar visible) to decide panel position
  const activeView = useUIStore((s) => s.activeView);
  const isInboxView = activeView === 'CONVERSATIONS' || activeView === 'CHAT';

  // Shared users only see Dialer & History — no Settings
  const user = useAuthStore((s) => s.user);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const isAdmin = !selectedAdminUserId || selectedAdminUserId === user?.uid;
  const tabs = useMemo(
    () => (isAdmin ? ALL_TABS : ALL_TABS.filter((t) => t.id !== 'settings')),
    [isAdmin]
  );
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking the phone button in InboxNav
      if (target.closest('[data-call-manager-trigger]')) return;
      if (panelRef.current && !panelRef.current.contains(target)) {
        toggleCallManager();
      }
    };

    // Delay to avoid immediate trigger
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [isOpen, toggleCallManager]);

  // Load agent statuses — temporarily disabled until CF writes agent statuses
  // useEffect(() => {
  //   if (isOpen && isAdmin) {
  //     loadAgentStatuses();
  //   }
  // }, [isOpen, isAdmin, loadAgentStatuses]);

  // Fresh-fetch phone numbers & voice config when dialog opens
  useEffect(() => {
    if (isOpen && softphoneState === 'registered') {
      refreshVoiceConfig();
    }
  }, [isOpen, softphoneState, refreshVoiceConfig]);

  // Auto-populate dialer with CRM phone number when call manager opens in CRM context.
  // Fill at most once per open per record fields — erasing the number to empty
  // must NOT re-trigger the fill (the effect must not depend on dialerNumber).
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const crmPhoneFields = useUIStore((s) => s.crmPhoneFields);
  const crmSelectedPhoneField = useUIStore((s) => s.crmSelectedPhoneField);
  const setDialerNumber = useCallStore((s) => s.setDialerNumber);
  const autoFilledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      autoFilledKeyRef.current = null;
      return;
    }
    if (!crmRecordContext || crmPhoneFields.length === 0) return;
    const fillKey = `${crmSelectedPhoneField}|${crmPhoneFields.map((f) => f.value).join(',')}`;
    if (autoFilledKeyRef.current === fillKey) return;
    autoFilledKeyRef.current = fillKey;
    if (useCallStore.getState().dialerNumber) return; // Don't overwrite if already filled
    const selected = crmPhoneFields.find((f) => (f.key || f.apiName) === crmSelectedPhoneField);
    const phone = selected?.value || crmPhoneFields[0]?.value;
    if (phone) {
      // Dial the contact number exactly as stored — don't strip the leading '+'
      // or any digits. Stripping the '+' would let Twilio re-default to US (+1).
      setDialerNumber(phone.trim());
    }
  }, [isOpen, crmRecordContext, crmPhoneFields, crmSelectedPhoneField, setDialerNumber]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') toggleCallManager();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, toggleCallManager]);

  // const onlineAgentCount = useMemo(
  //   () => Object.values(agentStatuses).filter((s) => s.available).length,
  //   [agentStatuses]
  // );

  if (!isOpen || hasActiveCalls) return null;

  return (
    <div
      ref={panelRef}
      data-testid="call-manager"
      className={cn(
        'fixed z-[55] bg-background border border-border rounded-xl shadow-2xl overflow-hidden',
        'flex flex-col',
        isAdmin ? 'w-[400px]' : 'w-[340px]',
        'min-h-[400px] max-h-[calc(100vh-5rem)]',
        // Inbox view: anchor near sidebar on the left
        // Compose/other views: anchor near header phone button on the right
        isInboxView
          ? 'top-16 left-16 md:left-[60px]'
          : 'top-16 right-4'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <h2 className="text-sm font-semibold text-foreground">Call Manager</h2>
        <button
          onClick={toggleCallManager}
          className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Availability toggle — always visible; toggling ON triggers voice init */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          {softphoneState === 'registering' ? (
            <>
              <span className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-xs font-medium text-foreground">Connecting…</span>
            </>
          ) : (
            <>
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  isAvailable ? 'bg-emerald-500' : 'bg-gray-400'
                )}
              />
              <span className="text-xs font-medium text-foreground">
                {isAvailable ? 'Available' : 'Unavailable'}
              </span>
            </>
          )}
        </div>
        <button
          onClick={toggleAvailability}
          disabled={softphoneState === 'registering' || availabilityUpdating}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors',
            softphoneState === 'registering' || availabilityUpdating
              ? 'bg-blue-300 dark:bg-blue-600 opacity-60 cursor-wait'
              : isAvailable ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
          )}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform',
              isAvailable ? 'translate-x-[18px]' : 'translate-x-[3px]'
            )}
          />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setTab(tab.id)}
            className={cn(
              'flex-1 flex flex-col items-center justify-center gap-0.5 px-3 py-2 text-xs font-medium transition-colors',
              activeTab === tab.id
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <div className="flex items-center gap-1.5">
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </div>
            {/* {tab.id === 'agents' && (
              <span className="text-[10px] font-normal text-emerald-600 dark:text-emerald-400 leading-none">
                {onlineAgentCount} online
              </span>
            )} */}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'dialer' && <Dialer />}
        {activeTab === 'history' && <CallHistory compact />}
        {/* {activeTab === 'agents' && isAdmin && <AgentAvailabilityList />} */}
        {activeTab === 'settings' && isAdmin && <VoiceSettings />}
      </div>
    </div>
  );
}
